import React, { useEffect, useRef, useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { Flex, Box } from 'rebass';
import dayjs from 'dayjs';

import Button from '../../components/Button/Button';
import FormButton from '../../components/FormButton/FormButton';
import FormInput from '../../components/FormInput/FormInput';
import ClockIcon from '../../components/Icon/Clock';
import PinIcon from '../../components/Icon/Pin';
import Markup from '../../base/Markup/Markup';
import Select from '../../components/Select/Select';
import Typography, {
  TypographyContextProvider,
} from '../../components/Typography/Typography';
import WideContainer from '../../components/WideContainer/WideContainer';

import colourMatching from '../../utils/colourMatching';
import { JobsSectionStoryblok } from '../../types/storyblok';

// This is a static list defined by https://dev.smartrecruiters.com/company-api/endpoints/functions/
// Some of these are not needed and as such are commented out.
const functionTypes = [
  'Accounting/Auditing',
  'Administrative',
  'Advertising',
  'Analyst',
  'Art/Creative',
  'Business Development',
  'Consulting',
  'Customer Service',
  'Design',
  'Distribution',
  'Education',
  'Engineering',
  'Finance',
  'General Business',
  // 'Health Care Provider',
  'Human Resources',
  'Information Technology',
  'Legal',
  'Management',
  'Manufacturing',
  'Marketing',
  'Other',
  'Production',
  'Product Management',
  'Project Management',
  'Public Relations',
  'Purchasing',
  'Quality Assurance',
  'Research',
  'Sales',
  'Science',
  'Strategy/Planning',
  'Supply Chain',
  'Training',
  'Writing/Editing',
];

const getStartupData = () => {
  const startupData = useStaticQuery(graphql`
    {
      records: allAirtable(
        filter: {
          table: { eq: "Master Companies" }
          data: { SmartRecruiter_ID: { ne: null } }
        }
        sort: { data: { Company_Name: ASC } }
      ) {
        nodes {
          id
          table
          content: data {
            name: Company_Name
            srId: SmartRecruiter_ID
            logo: Logo {
              raw {
                type
                url
              }
              images: localFiles {
                childImageSharp {
                  gatsbyImageData(height: 64)
                }
              }
            }
          }
        }
      }
    }
  `);
  return startupData.records.nodes;
};

const getStartupNames = (startups) =>
  startups.map((startup) => startup.content.name);

const getStartupIds = (startups) =>
  startups.map((startup) => startup.content.srId);

const getPostingData = (postings) =>
  Object.values(postings).sort(
    (a, b) => new Date(b.releasedDate) - new Date(a.releasedDate)
  );

const PostingCard = ({
  company,
  companyLogo,
  location,
  name,
  postingUrl,
  releasedDate,
  typeOfEmployment,
}: JobsSectionStoryblok) => {
  const image = getImage(companyLogo.images.slice(-1)[0]); // last element should be the most recent
  return (
    <Flex
      onClick={() => {
        window.open(postingUrl, '_blank').focus();
      }}
      width={1}
      px={[4, , , 5]}
      py={[3, , , 5]}
      flexDirection="row"
      fontSize={['0.7rem', , '0.9rem']}
      alignItems="center"
      sx={{
        border: '1px solid',
        borderColor: 'greyText',
      }}
    >
      <Box ml={[, , 2]} mr={[4, , 9]} width={['2.5rem', , '6rem']}>
        <GatsbyImage image={image} alt={company.name} />
      </Box>
      <Flex flexDirection="column">
        <Typography component="strong">{name}</Typography>
        <Typography inline>
          {typeOfEmployment.label} | {company.name}
        </Typography>
        <Typography
          inline
          alignItems="baseline"
          display="inline-flex"
          color="#6b6b6b"
        >
          <PinIcon style={{ marginRight: '0.4rem' }} />
          {location.city}
          <ClockIcon
            style={{
              marginLeft: '1rem',
              marginRight: '0.4rem',
              marginTop: '0.05rem',
            }}
          />
          {dayjs(releasedDate).format('MMMM YYYY')}
        </Typography>
      </Flex>
      <Box display={['none', , 'block']} ml="auto">
        <Button arrow href={postingUrl}>
          Read more
        </Button>
      </Box>
    </Flex>
  );
};

/**
 * Renders a <JobsSection /> component
 * @param {Object} props
 * @param {string} [props.colourScheme]
 * @param {boolean} [props.hideCompanyFilter]
 * @param {boolean} [props.hideJobTypeFilter]
 * @param {number} [props.itemsPerPage]
 * @param {Markup} [props.markup]
 * @param {Startup[]} props.startups
 */
const JobsSection = ({
  colourScheme,
  hideCompanyFilter,
  hideJobTypeFilter,
  itemsPerPage: itemsPerPageRaw,
  markup,
}) => {
  const startups = getStartupData();
  const sectionEl = useRef(null);
  const itemsPerPage = parseInt(itemsPerPageRaw) || 10;
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(null);
  const [postings, setPostings] = useState({});
  const [searchQueryInput, setSearchQueryInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [companyFilter, setCompanyFilter] = useState();
  const [jobTypeFilter, setJobTypeFilter] = useState();

  const startupIds = startups ? getStartupIds(startups) : [];

  const updatePageIndex = (index) => {
    setCurrentPage(index);
    sectionEl.current.scrollIntoView({
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (searchQuery === null) {
      return;
    }

    setIsLoading(true);

    Promise.all(
      startupIds.map(async (startupId) => {
        const res = await fetch(
          `https://api.smartrecruiters.com/v1/companies/${startupId}/postings${
            searchQuery ? `?q=${searchQuery}` : ''
          }`
        );

        if (!res.ok) {
          return null;
        }

        return res.json();
      })
    ).then((startupPostings) => {
      const postings = startupPostings
        .map((startupPosting) => (startupPosting ? startupPosting.content : []))
        .flat();

      setPostings(
        postings.reduce(
          (acc, posting) => ({
            ...acc,
            [posting.uuid]: {
              ...acc[posting.uuid],
              ...posting,
              companyLogo: startups.find(
                (startup) => startup.content.srId === posting.company.identifier
              )?.content.logo,
            },
          }),
          {}
        )
      );

      setIsLoading(false);
      setSearchQuery(null);
    });
  }, [JSON.stringify(startupIds), searchQuery]);

  useEffect(() => {
    if (isLoading !== false) {
      return;
    }

    Object.values(postings).forEach(async (posting) => {
      if (posting.postingUrl || posting.postingUrl === null) {
        return;
      }

      setPostings((current) => ({
        ...current,
        [posting.uuid]: {
          ...current[posting.uuid],
          postingUrl: null,
        },
      }));

      const res = await fetch(posting.ref);

      if (!res.ok) {
        return;
      }

      const resData = await res.json();

      setPostings((current) => ({
        ...current,
        [resData.uuid]: {
          ...current[resData.uuid],
          postingUrl: resData.postingUrl,
        },
      }));
    });
  }, [isLoading, postings]);

  const postingData = getPostingData(postings);
  const filteredPostings = postingData
    .filter(({ company }) =>
      companyFilter
        ? company.identifier ===
          startups.find((startup) => startup.content.name === companyFilter)
            ?.content.srId
        : true
    )
    .filter(({ function: functionType }) =>
      functionType?.label && jobTypeFilter
        ? functionType.label === jobTypeFilter
        : true
    );

  return (
    <TypographyContextProvider value={{ colourScheme }}>
      <Box
        backgroundColor={colourMatching(colourScheme)}
        px={[6, , , 9]}
        pb={7}
      >
        <WideContainer>
          <Box maxWidth="42.5rem">{markup && <Markup content={markup} />}</Box>
          <Flex
            as="form"
            width="100%"
            mt={[7, , , 8]}
            mb={4}
            maxWidth="29rem"
            onSubmit={(e) => {
              e.preventDefault();
              setSearchQuery(searchQueryInput);
              setSearchQueryInput('');
            }}
            sx={{
              height: '2.5rem',
              '&&& input': {
                backgroundColor: 'white',
                height: '100%',
              },
              '&&& button': {
                backgroundColor: 'white',
                height: '100%',
              },
            }}
          >
            <Box flexGrow="2">
              <FormInput
                value={searchQueryInput}
                width="100%"
                placeholder="Search job title, companies, industry..."
                onChange={(e) => setSearchQueryInput(e.target.value)}
              />
            </Box>
            <Box>
              <FormButton grouped type="submit">
                <Typography inline>Go</Typography>
              </FormButton>
            </Box>
          </Flex>
          {hideCompanyFilter ? null : (
            <Box
              mb={[3, , 0]}
              mr={[, 4]}
              display="inline-block"
              sx={{
                '& > button': {
                  backgroundColor: 'white',
                  maxWidth: ['12.5rem', 'unset'],
                  '& > .options-box': {
                    maxHeight: ['10rem', , '14rem'],
                    maxWidth: ['12.5rem', 'unset'],
                    overflow: 'auto',
                  },
                },
              }}
            >
              <Select
                emptyLabel="All"
                prompt="Company"
                value={companyFilter}
                values={getStartupNames(startups).sort((a, b) =>
                  a.localeCompare(b)
                )}
                onChange={(value) => {
                  setCompanyFilter(value);
                }}
              />
            </Box>
          )}
          {hideJobTypeFilter ? null : (
            <Box
              display="inline-block"
              sx={{
                '& > button': {
                  backgroundColor: 'white',
                  maxWidth: ['12.5rem', 'unset'],
                  '& > .options-box': {
                    maxHeight: ['9.6rem', , '14rem'],
                    maxWidth: ['12.5rem', 'unset'],
                    overflow: 'auto',
                  },
                },
              }}
            >
              <Select
                emptyLabel="All"
                prompt="Job type"
                value={jobTypeFilter}
                values={functionTypes}
                onChange={(value) => {
                  setJobTypeFilter(value);
                }}
              />
            </Box>
          )}
        </WideContainer>
      </Box>
      {isLoading ? null : (
        <Box
          ref={sectionEl}
          as="section"
          px={[6, , , 9]}
          py={6}
          sx={{
            '& ol': {
              listStyle: 'none',
              margin: 0,
              padding: 0,
            },
            '& li:not(:last-child)': {
              mb: [2, , , 3],
            },
          }}
        >
          <WideContainer>
            <Typography>
              Showing{' '}
              <Typography component="strong">
                {filteredPostings.length}
              </Typography>{' '}
              jobs
            </Typography>
            <ol>
              {filteredPostings
                .slice(
                  currentPage * itemsPerPage,
                  (currentPage + 1) * itemsPerPage
                )
                .map(({ ref, ...posting }) => (
                  <li key={posting.uuid}>
                    <PostingCard {...posting} />
                  </li>
                ))}
            </ol>
            <Box width={1} pb={[, , , 4]} pt={[6, , , 8]}>
              <Flex
                justifyContent="space-between"
                mx="auto"
                fontSize="1rem"
                maxWidth="16rem"
              >
                <Typography
                  inline
                  variant="link"
                  onClick={() => {
                    if (currentPage <= 0) return;
                    updatePageIndex(currentPage - 1);
                  }}
                >
                  Back
                </Typography>
                <Typography inline px={[3, 4, 5]}>
                  {`${currentPage * itemsPerPage + 1} to ${Math.min(
                    (currentPage + 1) * itemsPerPage,
                    filteredPostings.length
                  )} of ${filteredPostings.length}`}
                </Typography>
                <Typography
                  inline
                  variant="link"
                  onClick={() => {
                    if (
                      (currentPage + 1) * itemsPerPage >=
                      filteredPostings.length
                    )
                      return;
                    updatePageIndex(currentPage + 1);
                  }}
                >
                  Next
                </Typography>
              </Flex>
            </Box>
          </WideContainer>
        </Box>
      )}
    </TypographyContextProvider>
  );
};

export default JobsSection;
